/**
 * Validation Helper Utilities
 * Shared functions for workflow validation
 */

import { Edge } from '@xyflow/react'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCode,
} from '@features/adventures/types/validation.types'

let issueCounter = 0

/**
 * Factory function to create a ValidationIssue with auto-generated ID
 */
export function createIssue(params: {
  nodeId: string | null
  nodeLabel?: string
  nodeType?: WorkflowNodeType
  severity: ValidationSeverity
  category: ValidationCategory
  code: ValidationErrorCode
  message: string
  details?: string
  handleId?: string
}): ValidationIssue {
  return {
    id: `issue-${++issueCounter}-${Date.now()}`,
    ...params,
  }
}

/**
 * Reset issue counter (useful for testing)
 */
export function resetIssueCounter(): void {
  issueCounter = 0
}

/**
 * Group edges by their source node ID
 */
export function groupEdgesBySource(edges: Edge[]): Map<string, Edge[]> {
  const map = new Map<string, Edge[]>()
  for (const edge of edges) {
    const existing = map.get(edge.source) || []
    existing.push(edge)
    map.set(edge.source, existing)
  }
  return map
}

/**
 * Group edges by their target node ID
 */
export function groupEdgesByTarget(edges: Edge[]): Map<string, Edge[]> {
  const map = new Map<string, Edge[]>()
  for (const edge of edges) {
    const existing = map.get(edge.target) || []
    existing.push(edge)
    map.set(edge.target, existing)
  }
  return map
}

/**
 * Check if a node has any outgoing edge
 */
export function hasOutgoingEdge(nodeId: string, edgeMap: Map<string, Edge[]>): boolean {
  return (edgeMap.get(nodeId)?.length || 0) > 0
}

/**
 * Check if a node has any incoming edge (optionally to a specific handle)
 */
export function hasIncomingEdge(
  nodeId: string,
  edgeMap: Map<string, Edge[]>,
  handleId?: string
): boolean {
  const edges = edgeMap.get(nodeId) || []
  if (!handleId) return edges.length > 0
  return edges.some(e => e.targetHandle === handleId || (!e.targetHandle && handleId === 'flow-target'))
}

/**
 * Check if a node has an outgoing edge from a specific handle
 */
export function hasOutgoingEdgeFromHandle(
  nodeId: string,
  handleId: string,
  edgeMap: Map<string, Edge[]>
): boolean {
  const edges = edgeMap.get(nodeId) || []
  return edges.some(e => e.sourceHandle === handleId || (!e.sourceHandle && handleId === 'flow-source'))
}

/**
 * Exit node types
 */
export const EXIT_NODE_TYPES = [
  WorkflowNodeType.Exit,
  WorkflowNodeType.ExitSuccess,
  WorkflowNodeType.ExitFailed,
  WorkflowNodeType.ExitAbandoned,
]

/**
 * Check if a node type is an exit node
 */
export function isExitNodeType(nodeType: WorkflowNodeType): boolean {
  return EXIT_NODE_TYPES.includes(nodeType)
}
